import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as LogoIcon } from "../../assets/logo/logo-centralis-gris.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/view-more.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/visibility-off.svg";
import Button from '../../composants/Button/Button';
// import CircularProgress from '@mui/material/CircularProgress';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // État pour la validation du mot de passe
  const [passwordValidation, setPasswordValidation] = useState({
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    isLongEnough: false,
    notEmail: true
  });
  
  // Extraire les paramètres de l'URL
  const getParamsFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const expires = searchParams.get('expires');
    const signature = searchParams.get('signature');
    
    return { token, email, expires, signature };
  };
  
  const { token, email, expires, signature } = getParamsFromUrl();

  // Vérification des paramètres à l'arrivée sur la page
  useEffect(() => {
    if (!token || !email || !expires || !signature) {
      setError("Le lien de réinitialisation est incomplet ou incorrect. Veuillez vérifier l'URL ou faire une nouvelle demande.");
    }
  }, [token, email, expires, signature]);

  // Vérification de la correspondance des mots de passe
  useEffect(() => {
    setPasswordsMatch(
      password === passwordConfirmation &&
      password !== ''
    );
  }, [password, passwordConfirmation]);

  // Fonction pour valider la complexité du mot de passe
  const validatePassword = (password: string) => {
    setPasswordValidation({
      hasLowerCase: /[a-z]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      isLongEnough: password.length >= 8,
      notEmail: !/\S+@\S+\.\S+/.test(password)
    });
  };

  // Gestion du changement de mot de passe
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  // Vérifier si le mot de passe est valide selon tous les critères
  const isPasswordValid = () => {
    return Object.values(passwordValidation).every(value => value === true);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Vérification des paramètres requis
    if (!token || !email || !expires || !signature) {
      setError("Paramètres de réinitialisation manquants. Veuillez vérifier le lien reçu par email.");
      return;
    }

    // Vérification de la validité et correspondance des mots de passe
    if (!isPasswordValid()) {
      setError("Le mot de passe ne respecte pas les critères de sécurité.");
      return;
    }

    if (!passwordsMatch) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // URL de l'API pour la réinitialisation du mot de passe
      const resetUrl = `${process.env.REACT_APP_API_URL}/reset-password`;
      
      const response = await fetch(resetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          token,
          email: decodeURIComponent(email),
          password,
          password_confirmation: passwordConfirmation,
          expires,
          signature
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Une erreur est survenue lors de la réinitialisation');
      }
      
      setMessage("Votre mot de passe a été réinitialisé avec succès !");
      
      // Redirection vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Une erreur inconnue est survenue lors de la réinitialisation du mot de passe");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="logo-container">
          <LogoIcon className="logo" />
        </div>
        <h1>Réinitialisation du mot de passe</h1>
        
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        {!message && (
          <form onSubmit={handleResetPassword} className="reset-password-form">
            <div className="reset-password-content">
              <p>Veuillez entrer votre nouveau mot de passe.</p>
              
              <div className="form-group">
                <label htmlFor="password">Nouveau mot de passe<span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Entrez votre nouveau mot de passe"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                <div className="password-requirements">
                  <p>Le mot de passe doit contenir :</p>
                  <ul>
                    <li className={passwordValidation.hasLowerCase ? 'valid' : 'invalid'}>Une minuscule</li>
                    <li className={passwordValidation.hasUpperCase ? 'valid' : 'invalid'}>Une majuscule</li>
                    <li className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>Un chiffre</li>
                    <li className={passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}>Un caractère spécial</li>
                    <li className={passwordValidation.isLongEnough ? 'valid' : 'invalid'}>Au moins 8 caractères</li>
                    <li className={passwordValidation.notEmail ? 'valid' : 'invalid'}>Ne doit pas être une adresse email</li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password_confirmation">Confirmer le mot de passe<span style={{ color: 'red' }}>*</span></label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="password_confirmation"
                    name="password_confirmation"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    placeholder="Confirmez votre nouveau mot de passe"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                {!passwordsMatch && passwordConfirmation !== '' && (
                  <div className="error-message">
                    Les mots de passe ne correspondent pas.
                  </div>
                )}
              </div>
              
              {isLoading ? (
                <div className="loading-container">
                  {/* <CircularProgress /> */}
                  <p>Réinitialisation en cours...</p>
                </div>
              ) : (
                <Button
                  type="submit"
                  variant="primary"
                  value="Réinitialiser le mot de passe"
                  size="medium"
                  disabled={!isPasswordValid() || !passwordsMatch || !token || isLoading}
                />
              )}
            </div>
          </form>
        )}
        
        <div className="reset-password-footer">
          <p>Si vous rencontrez des problèmes, veuillez contacter notre support.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;